/** Mobile tab strips scroll natively and bring the selected tab towards the centre.
 * Ant's transform scroller is disabled by the matching mobile CSS only.
 */
export function installMobileTabs(root: Document = document): () => void {
  const media = window.matchMedia('(max-width: 768px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strips = '.ant-tabs-nav-wrap, [role="tablist"]:not(.ant-tabs-nav)';
  const selected = new WeakMap<HTMLElement, Element>();
  const watched = new WeakSet<Element>();
  let frame = 0;
  let pending: HTMLElement | null = null;
  const wrapFor = (target: EventTarget | null): HTMLElement | null => {
    if (!(target instanceof Element)) return null;
    return target.closest<HTMLElement>(
      '.ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap, ' +
        '.ant-tabs-bottom > .ant-tabs-nav .ant-tabs-nav-wrap',
    ) || target.closest<HTMLElement>('[role="tablist"]:not(.ant-tabs-nav)');
  };
  const tabBox = (tab: HTMLElement) => tab.closest<HTMLElement>('.ant-tabs-tab') || tab;
  // Tabs stay left-aligned; the selected tab is centred only as far as the strip can scroll,
  // so tabs left of the middle stay put and later ones slide to the centre.
  const center = (tab: HTMLElement, wrap: HTMLElement) => {
    if (!wrap.clientWidth || wrap.scrollWidth <= wrap.clientWidth) return;
    const bounds = tab.getBoundingClientRect();
    const viewport = wrap.getBoundingClientRect();
    const delta = bounds.left + bounds.width / 2 - viewport.left - viewport.width / 2;
    wrap.scrollTo({
      left: wrap.scrollLeft + delta,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  };
  const update = () => {
    frame = 0;
    if (!media.matches) return;
    root.querySelectorAll<HTMLElement>(strips).forEach(wrap => {
      if (wrapFor(wrap) !== wrap) return;
      const list = wrap.querySelector(':scope > .ant-tabs-nav-list') || wrap;
      if (!watched.has(list)) {
        watched.add(list);
        sized.observe(list);
      }
      const active = wrap.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      if (active && wrapFor(active) === wrap && selected.get(wrap) !== active) {
        selected.set(wrap, active);
        center(tabBox(active), wrap);
      }
    });
    if (pending?.isConnected) {
      const wrap = wrapFor(pending);
      if (wrap) center(tabBox(pending), wrap);
    }
    pending = null;
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(update);
  };
  const activate = (event: Event) => {
    if (!media.matches || !(event.target instanceof Element)) return;
    const tab = event.target.closest<HTMLElement>('[role="tab"]');
    if (!tab || tab.getAttribute('aria-disabled') === 'true' || !wrapFor(tab)) return;
    if (event.type === 'focusin') {
      // rc-tabs resets scrollLeft in its bubbling focus handler. Native focus
      // and keyboard handlers still run; only that transform-scroller reset is skipped.
      if (tab.closest('.ant-tabs-nav-wrap')) event.stopPropagation();
      if (!tab.matches(':focus-visible')) return;
    }
    pending = tab;
    schedule();
  };
  // Let the browser own touch/wheel/scroll in the mobile strip. Otherwise Ant
  // prevents native touch scrolling or resets scrollLeft after a focus event.
  const nativeScroll = (event: Event) => {
    if (media.matches && wrapFor(event.target)) event.stopPropagation();
  };
  const resize = () => {
    root.querySelectorAll<HTMLElement>(strips).forEach(wrap => {
      selected.delete(wrap);
      if (!media.matches) wrap.scrollLeft = 0;
    });
    schedule();
  };
  // The selected tab widens (bold label, loaded counts) after the first scroll, which
  // raises the strip's scroll limit; centre it again once the size settles.
  const sized = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const wrap = wrapFor(entry.target);
      if (wrap) selected.delete(wrap);
    });
    schedule();
  });
  const observer = new MutationObserver(schedule);
  observer.observe(root.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['aria-selected'],
  });
  root.addEventListener('click', activate, true);
  root.addEventListener('focusin', activate, true);
  const nativeEvents = ['touchstart', 'touchmove', 'touchend', 'wheel', 'scroll'];
  nativeEvents.forEach(type => root.addEventListener(type, nativeScroll, { capture: true, passive: true }));
  window.addEventListener('resize', resize);
  schedule();
  return () => {
    observer.disconnect();
    sized.disconnect();
    window.cancelAnimationFrame(frame);
    root.removeEventListener('click', activate, true);
    root.removeEventListener('focusin', activate, true);
    nativeEvents.forEach(type => root.removeEventListener(type, nativeScroll, true));
    window.removeEventListener('resize', resize);
  };
}
